import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Mail, Phone, User, X, Save, MapPin, Calendar, UserCheck, Search, Building2, Users, Clock, Badge, Send } from 'lucide-react';
import Preloader from '../components/ui/Preloader';
import { useNavigate } from 'react-router-dom';
import useFetchIntakeCoordinator from '../hooks/Practice/useFetchIntakeCoordinator';
import InviteCoordinatorModal from './RegistrationPage/InviteCoordinatorModal';
import { isPending } from '@reduxjs/toolkit';

// Utility function to extract attribute value from Cognito user
const getAttributeValue = (attributes: any[], attributeName: string): string => {
    const attribute = attributes.find((attr: any) => attr.Name === attributeName);
    return attribute ? attribute.Value : '';
};

// Utility function to transform Cognito user to our format
const transformCognitoUser = (cognitoUser: any) => {
    const attributes = cognitoUser.Attributes || [];
    return {
        id: cognitoUser.Username,
        sub: getAttributeValue(attributes, 'sub'),
        email: getAttributeValue(attributes, 'email'),
        firstName: getAttributeValue(attributes, 'given_name'),
        lastName: getAttributeValue(attributes, 'family_name'),
        practiceId: getAttributeValue(attributes, 'custom:id_practice'),
        role: getAttributeValue(attributes, 'custom:user_role'),
        emailVerified: getAttributeValue(attributes, 'email_verified') === 'true',
        status: cognitoUser.UserStatus, // CONFIRMED, UNCONFIRMED, etc.
        enabled: cognitoUser.Enabled,
        createdDate: cognitoUser.UserCreateDate,
        lastModifiedDate: cognitoUser.UserLastModifiedDate,
        // Additional computed fields
        fullName: `${getAttributeValue(attributes, 'given_name')} ${getAttributeValue(attributes, 'family_name')}`,
        isActive: cognitoUser.Enabled && cognitoUser.UserStatus === 'CONFIRMED',
        isPending: cognitoUser.UserStatus === 'UNCONFIRMED' || !cognitoUser.Enabled
    };
};

const transformPracticeUser = (practiceUser: any) => {
    // Extract ObjectId from _id field
    let obj_id = '';
    try {
        obj_id = practiceUser._id?.$oid || '';
    } catch (error) {
        console.error('Error extracting ObjectId:', error);
        return {};
    }

    return {
        id: obj_id,
        id_practice: practiceUser.id_practice,
        id_cognito: practiceUser.id_cognito,
        email: practiceUser.email,
        first_name: practiceUser.first_name,
        last_name: practiceUser.last_name,
        invited_at: practiceUser.invited_at,
        accepted_at: practiceUser.accepted_at,
        role: practiceUser.role,
        status: practiceUser.status,
        isActive: true,
        isPending: false
    };
};


// Define types for form data
interface FormData {
    name: string;
    practice: string;
    email: string;
    phone: string;
    hireDate: string;
    status: string;
    specialties: string[];
    location: string;
    workingHours: string;
}

// Define types for form data
interface UserFormData {
    id: string;
    first_name: string;
    last_name: string;
    id_practice: string;
    id_cognito: string;
    email: string;
    role: string;
    status: string;
}

interface InviteData {
    email: string;
    firstName: string;
    lastName: string;
    practice: string;
    location: string;
    workingHours: string;
    message: string;
}

interface TransformedUser {
    id: string;
    sub: string;
    email: string;
    firstName: string;
    lastName: string;
    practiceId: string;
    role: string;
    emailVerified: boolean;
    status: string;
    enabled: boolean;
    createdDate: string;
    lastModifiedDate: string;
    fullName: string;
    isActive: boolean;
    isPending: boolean;
}

interface PracticeUser {
    id: string;
    id_practice: string;
    id_cognito: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    status: string;    
    invited_at: string;
    accepted_at: string;
}

const TeamPage: React.FC = () => {
    const navigate = useNavigate();
    const { coordinators, isLoading, fetchIntakeCoordinator } = useFetchIntakeCoordinator();

    const [showAddForm, setShowAddForm] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [editingMember, setEditingMember] = useState<TransformedUser | null>(null);
    const [editingUser, setEditingUser] = useState<PracticeUser | null>(null);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        practice: '',
        email: '',
        phone: '',
        hireDate: '',
        status: 'Active',
        specialties: [],
        location: '',
        workingHours: ''
    });
    const [userformData, setUserFormData] = useState<UserFormData>({
        id: '',
        first_name: '',
        last_name: '',
        id_practice: '',
        id_cognito: '',
        email: '',
        role: '',
        status: 'Active'
    });

    const [inviteData, setInviteData] = useState<InviteData>({
        email: '',
        firstName: '',
        lastName: '',
        practice: '',
        location: '',
        workingHours: '',
        message: ''
    });

    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Practices list for dropdown
    const practices: string[] = [];

    const locations: string[] = [];

    // Intake-specific specialties
    const intakeSpecialties = [
        'Patient Intake',
        'Insurance Verification',
        'Benefits Verification',
        'Prior Authorization',
        'Appointment Scheduling',
        'Claims Support',
        'Customer Service',
        'Medical Records',
        'Patient Registration',
        'Referral Coordination'
    ];

    const workingHourOptions = [
        '8 AM - 4 PM',
        '9 AM - 5 PM',
        '10 AM - 6 PM',
        '7 AM - 3 PM',
        'Flexible Hours',
        'Part-time'
    ];

    const practiceId = JSON.parse(localStorage.getItem('user') || '{}')?.["custom:id_practice"];

    useEffect(() => {
        if (practiceId) {
            fetchIntakeCoordinator(practiceId);
        }
    }, [practiceId, fetchIntakeCoordinator]);

    // Transform coordinators data
    const transformedCoordinators = React.useMemo(() => {
        // if (!coordinators?.users || !Array.isArray(coordinators.users)) {
        if (!coordinators || !Array.isArray(coordinators) || coordinators.length === 0) {
            return [];
        }
        // return coordinators.users.map((user: any) => transformCognitoUser(user));
        return coordinators.map((user: any) => transformPracticeUser(user));
    }, [coordinators]);

    // Filter coordinators to only show intake coordinators
    const intakeCoordinators = React.useMemo(() => {
        // return transformedCoordinators.filter((user: PracticeUser) =>
        //     user.role === 'intake_coordinator' || user.role === 'practice_admin');
        return transformedCoordinators.filter((user) => user.role === 'intake_coordinator' || user.role === 'practice_admin');
    }, [transformedCoordinators]);

    // const resetForm = () => {
    //     setFormData({
    //         name: '',
    //         practice: '',
    //         email: '',
    //         phone: '',
    //         hireDate: '',
    //         status: 'Active',
    //         specialties: [],
    //         location: '',
    //         workingHours: ''
    //     });
    //     setShowAddForm(false);
    //     setEditingMember(null);
    // };

    const resetForm = () => {
        setFormData({
            name: '',
            practice: '',
            email: '',
            phone: '',
            hireDate: '',
            status: 'Active',
            specialties: [],
            location: '',
            workingHours: ''
        });
        setShowAddForm(false);
        setEditingMember(null);
    };

    const resetUserForm = () => {
        setUserFormData({
            id: '',
            id_cognito: '',
            id_practice: '',
            first_name: '',
            last_name: '',
            email: '',
            status: '',
            role: ''
        });
        setShowAddForm(false);
        setEditingMember(null);
    };

    const resetInviteForm = () => {
        setInviteData({
            email: '',
            firstName: '',
            lastName: '',
            practice: '',
            location: '',
            workingHours: '',
            message: ''
        });
        setShowInviteModal(false);
    };

    const handleSubmit = () => {
        // Basic validation
        if (!formData.name || !formData.practice || !formData.email || !formData.phone || !formData.hireDate || !formData.location || !formData.workingHours) {
            alert('Please fill in all required fields');
            return;
        }

        if (editingMember) {
            // Update existing member logic here
        } else {
            // Add new member logic here
        }

        resetForm();
    };

    const handleInviteSubmit = () => {
        // Basic validation
        if (!inviteData.email || !inviteData.firstName || !inviteData.lastName || !inviteData.practice || !inviteData.location || !inviteData.workingHours) {
            alert('Please fill in all required fields');
            return;
        }

        // Handle invite logic here
        alert('Invitation sent successfully!');
        resetInviteForm();
    };

    const handleEdit = (member: TransformedUser) => {
        setFormData({
            practice: '',
            email: member.email,
            phone: '',
            hireDate: '',
            status: member.isActive ? 'Active' : 'Inactive',
            specialties: [],
            location: '',
            workingHours: '',
            name: ''
        });
        setEditingMember(member);
        setShowAddForm(true);
    };

    const handleEditUser = (member: any) => {
        setUserFormData({
            id: member.id,
            id_practice: member.id_practice,
            id_cognito: member.id_cognito,
            first_name: member.first_name,
            last_name: member.last_name,
            email: member.email,
            status: member.status,
            role: member.role
        });
        setEditingUser(member);
        setShowAddForm(true);
    };

    const handleDelete = (id: string) => {
        if (window.confirm('Are you sure you want to remove this intake coordinator?')) {
            // Handle delete logic here
        }
    };

    const handleDeleteUser = (id: string) => {
        if (window.confirm('Are you sure you want to remove this intake coordinator?')) {
            // Handle delete logic here
        }
    };

    const handleSpecialtyToggle = (specialty: string) => {
        // setFormData(prev => ({
        //     ...prev,
        //     specialties: prev.specialties.includes(specialty)
        //         ? prev.specialties.filter(s => s !== specialty)
        //         : [...prev.specialties, specialty]
        // }));
    };

    const filteredMembers = React.useMemo(() => {
        // return intakeCoordinators.filter((member: TransformedUser) => {
        //     const matchesStatus = filterStatus === 'all' ||
        //         (filterStatus === 'Active' && member.isActive) ||
        //         (filterStatus === 'Pending' && member.isPending);

        //     const matchesSearch = member.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        //         member.email.toLowerCase().includes(searchTerm.toLowerCase());

        //     return matchesStatus && matchesSearch;
        // });

        return intakeCoordinators.filter((member) => {
            const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'Active' && member.isActive) ||
                (filterStatus === 'Pending' && member.isPending);

            const matchesSearch = member.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                member.email.toLowerCase().includes(searchTerm.toLowerCase());

            return matchesStatus && matchesSearch;
        });
    }, [intakeCoordinators, filterStatus, searchTerm]);

    // Calculate stats
    const totalCoordinators = intakeCoordinators.length;
    // const activeCoordinators = intakeCoordinators.filter((member: TransformedUser) => member.isActive).length;
    // const pendingCoordinators = intakeCoordinators.filter((member: TransformedUser) => member.isPending).length;
    const activeCoordinators = intakeCoordinators.filter((member) => member.isActive).length;
    const pendingCoordinators = intakeCoordinators.filter((member) => member.isPending).length;

    if (isLoading) {
        return <Preloader />
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className=""
        >
            <div className=" mx-auto space-y-6">
                {/* Header Section */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                        <div className="space-y-1">
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                    <Users className="h-5 w-5 text-white" />
                                </div>
                                Team Members
                            </h1>
                            <p className="text-gray-600 text-lg">Manage team members across all practice locations</p>
                        </div>
                        <button
                            onClick={() => setShowInviteModal(true)}
                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                            <Plus className="h-5 w-5 mr-2" />
                            Add Coordinator
                        </button>
                    </div>

                    {/* Enhanced Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold text-blue-700">{totalCoordinators}</div>
                                    <div className="text-blue-600 font-medium">Total Team Members</div>
                                </div>
                                <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                                    <Users className="h-6 w-6 text-blue-700" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold text-green-700">{activeCoordinators}</div>
                                    <div className="text-green-600 font-medium">Active Team Members</div>
                                </div>
                                <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                                    <UserCheck className="h-6 w-6 text-green-700" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl border border-amber-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold text-amber-700">{pendingCoordinators}</div>
                                    <div className="text-amber-600 font-medium">Pending Team Members</div>
                                </div>
                                <div className="w-12 h-12 bg-amber-200 rounded-lg flex items-center justify-center">
                                    <Clock className="h-6 w-6 text-amber-700" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Search and Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            {[
                                { key: 'all', label: 'All', count: totalCoordinators },
                                { key: 'Active', label: 'Active', count: activeCoordinators },
                                { key: 'Pending', label: 'Pending', count: pendingCoordinators }
                            ].map((filter) => (
                                <button
                                    key={filter.key}
                                    onClick={() => setFilterStatus(filter.key)}
                                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 ${filterStatus === filter.key
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {filter.label} ({filter.count})
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Enhanced Team Members Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredMembers.length === 0 ? (
                        <div className="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No coordinators found</h3>
                            <p className="text-gray-500 mb-4">
                                {searchTerm ? 'No coordinators match your search criteria.' : 'Get started by adding your first intake coordinator.'}
                            </p>
                            <button
                                onClick={() => setShowInviteModal(true)}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Coordinator
                            </button>
                        </div>
                    ) : (
                        // filteredMembers.map((member: TransformedUser, index: number) => (
                        filteredMembers.map((member, index: number) => (
                            <motion.div
                                key={member.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-300 overflow-hidden"
                            >
                                {/* Card Header */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                                                <UserCheck className="h-6 w-6 text-white" />
                                            </div>
                                            <div>
                                                {/* <h3 className="font-bold text-gray-900 text-lg">{member.fullName}</h3> */}
                                                <h3 className="font-bold text-gray-900 text-lg">{member.first_name} {member.last_name}</h3>
                                                <p className="text-blue-600 font-medium text-sm">
                                                    {member.role === 'practice_admin' ? 'Practice Admin' : 'Intake Coordinator'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => handleEditUser(member)}
                                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                            >
                                                <Edit2 className="h-4 w-4" />
                                            </button>
                                            {member.role !== 'practice_admin' && (
                                                <button
                                                    onClick={() => handleDelete(member.id?.toString() || '')}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className="p-6">
                                    <div className="space-y-3">
                                        <div className="flex items-center text-gray-600">
                                            <Mail className="h-4 w-4 mr-3 text-gray-400" />
                                            <span className="text-sm truncate">{member.email}</span>
                                        </div>

                                        <div className="flex items-center justify-between pt-2">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${member.isActive
                                                ? 'bg-green-100 text-green-800 border border-green-200'
                                                : member.isPending
                                                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                                                }`}>
                                                {member.isActive ? 'Active' : member.isPending ? 'Pending' : 'Inactive'}
                                            </span>

                                            <div className="flex items-center text-xs text-gray-500">
                                                {member.status ? (
                                                    <Badge className="h-3 w-3 text-green-500 mr-1" />
                                                ) : (
                                                    <Badge className="h-3 w-3 text-amber-500 mr-1" />
                                                )}
                                                {member.status ? 'Verified' : 'Unverified'}
                                            </div>
                                        </div>

                                        <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                                            Created: {new Date(member.accepted_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))
                    )}
                </div>

                {/* Enhanced Add/Edit Form Modal */}
                {showAddForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2 }}
                            className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                            <User className="h-4 w-4 text-white" />
                                        </div>
                                        {editingMember ? 'Edit Intake Coordinator' : 'Add New Intake Coordinator'}
                                    </h2>
                                    <button
                                        onClick={resetForm}
                                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Full Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.name}
                                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                placeholder="Enter full name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Practice *
                                            </label>
                                            <select
                                                value={formData.practice}
                                                onChange={(e) => setFormData(prev => ({ ...prev, practice: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            >
                                                <option value="">Select Practice</option>
                                                {practices.map(practice => (
                                                    <option key={practice} value={practice}>{practice}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                placeholder="Enter email address"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Phone *
                                            </label>
                                            <input
                                                type="tel"
                                                value={formData.phone}
                                                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                                placeholder="(555) 123-4567"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Location *
                                            </label>
                                            <select
                                                value={formData.location}
                                                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            >
                                                <option value="">Select Location</option>
                                                {locations.map(location => (
                                                    <option key={location} value={location}>{location}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Working Hours *
                                            </label>
                                            <select
                                                value={formData.workingHours}
                                                onChange={(e) => setFormData(prev => ({ ...prev, workingHours: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            >
                                                <option value="">Select Hours</option>
                                                {workingHourOptions.map(hours => (
                                                    <option key={hours} value={hours}>{hours}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Hire Date *
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.hireDate}
                                                onChange={(e) => setFormData(prev => ({ ...prev, hireDate: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Status
                                            </label>
                                            <select
                                                value={formData.status}
                                                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                            >
                                                <option value="Active">Active</option>
                                                <option value="On Leave">On Leave</option>
                                                <option value="Inactive">Inactive</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            Intake Specialties
                                        </label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto p-4 border border-gray-200 rounded-lg bg-gray-50">
                                            {intakeSpecialties.map(specialty => (
                                                <label key={specialty} className="flex items-center space-x-3 text-sm cursor-pointer hover:bg-white p-2 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.specialties.includes(specialty)}
                                                        onChange={() => handleSpecialtyToggle(specialty)}
                                                        className="rounded text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="truncate">{specialty}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                                <button
                                    onClick={resetForm}
                                    className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
                                >
                                    <Save className="h-4 w-4 mr-2" />
                                    {editingMember ? 'Update' : 'Add'} Coordinator
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
                <InviteCoordinatorModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} onSuccess={() => { }} />

            </div>
        </motion.div>
    );
};

export default TeamPage;
